/**
 * Live model catalogs — context limits from Google Gemini and OpenRouter APIs.
 *
 * - Google: GET generativelanguage.googleapis.com/v1beta/models (`inputTokenLimit`)
 * - OpenRouter: GET openrouter.ai/api/v1/models (`context_length`, includes ~latest aliases)
 *
 * Used by edge brain (truncation/summarization) and can be warmed from apps via
 * `warmModelCatalogCaches()`.
 */

import { AI_MODELS, GEMINI_MODELS } from './ai-settings.ts';
import type { CatalogModel } from './ai-settings.ts';

/** Fallback when live catalog fetch fails — matches `GEMINI_MODELS` flash/lite window. */
export const DEFAULT_GEMINI_CONTEXT_LIMIT = 1_048_576;
export const DEFAULT_OPENROUTER_CONTEXT_LIMIT = 128_000;

export type ModelContextProvider = 'gemini' | 'openrouter';

/** Capability flags on a `CatalogModel` row — shared by the brain `/model-catalog` builder. */
export type ModelCapabilities = CatalogModel['capabilities'];

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const GEMINI_MODELS_URL = 'https://generativelanguage.googleapis.com/v1beta/models';
const OPENROUTER_MODELS_URL = 'https://openrouter.ai/api/v1/models';

type CatalogCache = {
  map: Map<string, number>;
  expiresAt: number;
};

/** Rich per-model metadata from the OpenRouter catalog (one row per model id). */
export interface OpenRouterModelDetails {
  contextLength?: number;
  /** USD per 1M tokens — converted from OpenRouter's per-token USD strings. */
  costPer1M?: { input: number; output: number };
  supportedParameters: string[];
  inputModalities: string[];
  /** Model creation time, unix seconds. */
  created?: number;
  /** Display name from the catalog ("Anthropic: Claude Sonnet 4.6"). */
  name?: string;
  description?: string;
}

/** One canonical catalog row — the full model list, deduped by API id. */
export interface OpenRouterCatalogEntry {
  id: string;
  details: OpenRouterModelDetails;
}

/** Combined result of one OpenRouter catalog fetch (single HTTP round-trip). */
interface OpenRouterCatalogData {
  limits: Map<string, number>;
  details: Map<string, OpenRouterModelDetails>;
  rows: OpenRouterCatalogEntry[];
}

let geminiCache: CatalogCache | null = null;
let openRouterCache: { data: OpenRouterCatalogData; expiresAt: number } | null = null;
let geminiInflight: Promise<Map<string, number>> | null = null;
let openRouterInflight: Promise<OpenRouterCatalogData> | null = null;

/** Static fallback when APIs are unavailable (offline / missing keys). */
function buildStaticFallback(provider: ModelContextProvider): Map<string, number> {
  const map = new Map<string, number>();
  if (provider === 'gemini') {
    for (const m of GEMINI_MODELS) {
      if ('contextWindow' in m && typeof m.contextWindow === 'number') {
        map.set(m.id, m.contextWindow);
        map.set(`google/${m.id}`, m.contextWindow);
        map.set(`models/${m.id}`, m.contextWindow);
      }
    }
    for (const m of AI_MODELS) {
      if (m.contextWindow) {
        map.set(m.id, m.contextWindow);
        const bare = m.id.replace(/^google\//, '');
        map.set(bare, m.contextWindow);
      }
    }
  }
  return map;
}

export function normalizeModelId(model: string): string {
  return model.replace(/^models\//, '').replace(/^google\//, '').trim();
}

function registerLimit(map: Map<string, number>, key: string, limit: number): void {
  if (!key || !Number.isFinite(limit) || limit <= 0) return;
  map.set(key, limit);
  const bare = normalizeModelId(key);
  if (bare !== key) map.set(bare, limit);
  if (!key.startsWith('google/')) map.set(`google/${bare}`, limit);
}

/** Longest-prefix / exact match against a catalog map. */
export function lookupContextLimit(
  map: Map<string, number>,
  model: string,
): number | undefined {
  const candidates = [
    model,
    normalizeModelId(model),
    `google/${normalizeModelId(model)}`,
    model.startsWith('~') ? model : undefined,
  ].filter((c): c is string => !!c);

  for (const id of candidates) {
    const hit = map.get(id);
    if (hit !== undefined) return hit;
  }

  const bare = normalizeModelId(model);
  let best: { key: string; limit: number } | undefined;
  for (const [key, limit] of map) {
    const keyBare = normalizeModelId(key);
    if (keyBare === bare || bare.startsWith(keyBare) || keyBare.startsWith(bare)) {
      if (!best || keyBare.length > normalizeModelId(best.key).length) {
        best = { key, limit };
      }
    }
  }
  return best?.limit;
}

interface GeminiModelRow {
  name?: string;
  baseModelId?: string;
  inputTokenLimit?: number;
}

interface GeminiListResponse {
  models?: GeminiModelRow[];
  nextPageToken?: string;
}

async function fetchGeminiCatalog(apiKey: string): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  let pageToken: string | undefined;

  do {
    const url = new URL(GEMINI_MODELS_URL);
    url.searchParams.set('key', apiKey);
    url.searchParams.set('pageSize', '100');
    if (pageToken) url.searchParams.set('pageToken', pageToken);

    const res = await fetch(url.toString());
    if (!res.ok) {
      throw new Error(`Gemini models.list failed: ${res.status} ${await res.text()}`);
    }

    const body = (await res.json()) as GeminiListResponse;
    for (const row of body.models ?? []) {
      const limit = row.inputTokenLimit;
      if (!limit) continue;
      const name = row.name?.replace(/^models\//, '') ?? '';
      if (name) registerLimit(map, name, limit);
      if (row.baseModelId) registerLimit(map, row.baseModelId, limit);
    }
    pageToken = body.nextPageToken;
  } while (pageToken);

  return map;
}

interface OpenRouterModelRow {
  id: string;
  canonical_slug?: string;
  name?: string;
  description?: string;
  context_length?: number | null;
  top_provider?: { context_length?: number | null };
  created?: number;
  supported_parameters?: string[];
  architecture?: { input_modalities?: string[] };
  pricing?: { prompt?: string; completion?: string };
}

interface OpenRouterListResponse {
  data?: OpenRouterModelRow[];
}

/** Convert an OpenRouter per-token USD string ("0.000003") to per-1M tokens (3). */
function perTokenToPerMillion(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  // Round to 4 decimal places to absorb float noise from the ×1e6 conversion.
  return Math.round(n * 1_000_000 * 10_000) / 10_000;
}

/** Register a details row under the same key variants `registerLimit` uses. */
function registerDetails(
  map: Map<string, OpenRouterModelDetails>,
  key: string,
  details: OpenRouterModelDetails,
): void {
  if (!key) return;
  map.set(key, details);
  const bare = normalizeModelId(key);
  if (bare !== key) map.set(bare, details);
  if (!key.startsWith('google/')) map.set(`google/${bare}`, details);
}

/** Fetch the OpenRouter catalog once, building both the context-limit and rich-details maps. */
async function fetchOpenRouterCatalogData(apiKey?: string): Promise<OpenRouterCatalogData> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;

  const res = await fetch(OPENROUTER_MODELS_URL, { headers });
  if (!res.ok) {
    throw new Error(`OpenRouter models failed: ${res.status} ${await res.text()}`);
  }

  const body = (await res.json()) as OpenRouterListResponse;
  const limits = new Map<string, number>();
  const details = new Map<string, OpenRouterModelDetails>();
  const rows: OpenRouterCatalogEntry[] = [];

  for (const row of body.data ?? []) {
    const limit =
      row.context_length ??
      row.top_provider?.context_length ??
      undefined;
    if (limit) {
      registerLimit(limits, row.id, limit);
      if (row.canonical_slug) registerLimit(limits, row.canonical_slug, limit);
    }

    const input = perTokenToPerMillion(row.pricing?.prompt);
    const output = perTokenToPerMillion(row.pricing?.completion);
    const rowDetails: OpenRouterModelDetails = {
      ...(limit ? { contextLength: limit } : {}),
      ...(input !== undefined || output !== undefined
        ? { costPer1M: { input: input ?? 0, output: output ?? 0 } }
        : {}),
      supportedParameters: row.supported_parameters ?? [],
      inputModalities: row.architecture?.input_modalities ?? [],
      ...(row.created ? { created: row.created } : {}),
      ...(row.name ? { name: row.name } : {}),
      ...(row.description ? { description: row.description } : {}),
    };
    registerDetails(details, row.id, rowDetails);
    if (row.canonical_slug) registerDetails(details, row.canonical_slug, rowDetails);
    rows.push({ id: row.id, details: rowDetails });
  }

  return { limits, details, rows };
}

function readEnv(key: string): string | undefined {
  const g = globalThis as {
    Deno?: { env: { get(k: string): string | undefined } };
    process?: { env?: Record<string, string | undefined> };
  };
  const fromDeno = g.Deno?.env.get(key);
  if (fromDeno) return fromDeno;
  return g.process?.env?.[key];
}

export function getGeminiCatalogApiKey(): string | undefined {
  return readEnv('GEMINI_API_KEY') ?? readEnv('GOOGLE_API_KEY');
}

export function getOpenRouterCatalogApiKey(): string | undefined {
  return readEnv('OPENROUTER_API_KEY');
}

async function getGeminiCatalog(): Promise<Map<string, number>> {
  const now = Date.now();
  if (geminiCache && geminiCache.expiresAt > now) {
    return geminiCache.map;
  }

  if (!geminiInflight) {
    geminiInflight = (async () => {
      const apiKey = getGeminiCatalogApiKey();
      let map: Map<string, number>;
      try {
        if (!apiKey) throw new Error('GEMINI_API_KEY not set');
        map = await fetchGeminiCatalog(apiKey);
        if (map.size === 0) throw new Error('empty Gemini catalog');
      } catch (err) {
        console.warn(
          '[model-catalog] Gemini catalog fallback:',
          err instanceof Error ? err.message : err,
        );
        map = buildStaticFallback('gemini');
      }
      geminiCache = { map, expiresAt: Date.now() + CACHE_TTL_MS };
      return map;
    })().finally(() => {
      geminiInflight = null;
    });
  }

  return geminiInflight;
}

async function getOpenRouterCatalogData(): Promise<OpenRouterCatalogData> {
  const now = Date.now();
  if (openRouterCache && openRouterCache.expiresAt > now) {
    return openRouterCache.data;
  }

  if (!openRouterInflight) {
    openRouterInflight = (async () => {
      const apiKey = getOpenRouterCatalogApiKey();
      let data: OpenRouterCatalogData;
      try {
        data = await fetchOpenRouterCatalogData(apiKey);
        if (data.limits.size === 0) throw new Error('empty OpenRouter catalog');
      } catch (err) {
        console.warn(
          '[model-catalog] OpenRouter catalog fallback:',
          err instanceof Error ? err.message : err,
        );
        data = { limits: buildStaticFallback('openrouter'), details: new Map(), rows: [] };
      }
      openRouterCache = { data, expiresAt: Date.now() + CACHE_TTL_MS };
      return data;
    })().finally(() => {
      openRouterInflight = null;
    });
  }

  return openRouterInflight;
}

async function getOpenRouterCatalog(): Promise<Map<string, number>> {
  return (await getOpenRouterCatalogData()).limits;
}

/**
 * Rich OpenRouter model metadata (cost, capabilities, creation date) keyed by
 * model id plus normalized variants (bare slug, `google/{bare}`), matching
 * `registerLimit` key normalization. Empty Map when the catalog fetch fails —
 * callers fall back to curated metadata.
 */
export async function getOpenRouterModelDetails(): Promise<Map<string, OpenRouterModelDetails>> {
  return (await getOpenRouterCatalogData()).details;
}

/**
 * The full deduped OpenRouter catalog — one entry per API model id, in API
 * order. Powers the complete model list in `GET /model-catalog`; empty array
 * when the catalog fetch fails (callers fall back to the curated shortlist).
 */
export async function getOpenRouterCatalogRows(): Promise<OpenRouterCatalogEntry[]> {
  return (await getOpenRouterCatalogData()).rows;
}

/** Look up a details row by any model-id form (exact, bare slug, google/ prefixed). */
export function lookupOpenRouterModelDetails(
  map: Map<string, OpenRouterModelDetails>,
  model: string,
): OpenRouterModelDetails | undefined {
  return map.get(model) ?? map.get(normalizeModelId(model));
}

/**
 * Infer `CatalogModel` capabilities from live OpenRouter metadata:
 * `reasoning` from `supported_parameters: 'reasoning'`, `tools` from `'tools'`,
 * `structuredOutput` from `'structured_outputs'`, `vision` when
 * `input_modalities` includes `'image'`.
 */
export function inferCapabilitiesFromOpenRouterDetails(
  details: OpenRouterModelDetails,
): ModelCapabilities {
  return {
    reasoning: details.supportedParameters.includes('reasoning'),
    tools: details.supportedParameters.includes('tools'),
    vision: details.inputModalities.includes('image'),
    structuredOutput: details.supportedParameters.includes('structured_outputs'),
  };
}

/** Static Gemini capability map for the brain catalog — all models support tools, vision, structured output. */
export function inferGeminiCapabilities(modelId: string): ModelCapabilities {
  return {
    reasoning:
      modelId.includes('-pro') ||
      modelId.includes('3.5-flash') ||
      modelId.includes('3.6-flash'),
    tools: true,
    vision: true,
    structuredOutput: true,
  };
}

const NEW_MODEL_WINDOW_SEC = 90 * 24 * 60 * 60; // 90 days

/**
 * Row badge inference, in priority order: `:free`/zero-cost → 'free',
 * `openrouter/auto` → 'auto', live `created` within 90 days → 'new',
 * id contains `preview`/`beta` → 'beta', gemini paid tier → 'paid'.
 */
export function inferCatalogStatus(input: {
  id: string;
  costPer1M?: { input: number; output: number };
  created?: number;
  geminiTier?: string;
  now?: number;
}): CatalogModel['status'] | undefined {
  const idLower = input.id.toLowerCase();
  if (idLower.includes(':free')) return 'free';
  if (input.costPer1M && input.costPer1M.input === 0 && input.costPer1M.output === 0) {
    return 'free';
  }
  if (input.id === 'openrouter/auto') return 'auto';
  const now = input.now ?? Date.now();
  if (input.created && now / 1000 - input.created < NEW_MODEL_WINDOW_SEC) return 'new';
  if (idLower.includes('preview') || idLower.includes('beta')) return 'beta';
  if (input.geminiTier === 'paid') return 'paid';
  return undefined;
}

/**
 * Search aliases for a catalog row: the vendor prefix, model-family tokens
 * from the id slug (split on `/`, `:`, `-`, `.`), name tokens, and the full
 * bare slug. E.g. `anthropic/claude-sonnet-5` →
 * `['anthropic', 'claude', 'sonnet', '5', 'claude-sonnet-5']`.
 */
export function deriveCatalogKeywords(id: string, name: string): string[] {
  const out: string[] = [];
  const push = (token: string): void => {
    const t = token.toLowerCase().trim();
    if (t && !out.includes(t)) out.push(t);
  };

  const slash = id.indexOf('/');
  if (slash !== -1) push(id.slice(0, slash));
  for (const token of id.split(/[/:\-.]+/)) push(token);
  if (slash !== -1) push(id.slice(slash + 1));
  for (const token of name.split(/[\s/:\-.()]+/)) push(token);

  return out;
}

/** Preload both catalogs (fire-and-forget safe). */
export async function warmModelCatalogCaches(): Promise<void> {
  await Promise.all([getGeminiCatalog(), getOpenRouterCatalog()]);
}

/**
 * Resolve input token budget from live provider catalogs.
 * OpenRouter `~vendor/family-latest` slugs use catalog metadata for the current target.
 */
export async function resolveModelContextLimit(
  model: string,
  options?: { provider?: ModelContextProvider },
): Promise<number> {
  const provider: ModelContextProvider =
    options?.provider ??
    (model.includes('/') || model.startsWith('~') ? 'openrouter' : 'gemini');

  if (provider === 'openrouter') {
    const map = await getOpenRouterCatalog();
    return lookupContextLimit(map, model) ?? DEFAULT_OPENROUTER_CONTEXT_LIMIT;
  }

  const map = await getGeminiCatalog();
  return (
    lookupContextLimit(map, model) ??
    lookupContextLimit(map, normalizeModelId(model)) ??
    DEFAULT_GEMINI_CONTEXT_LIMIT
  );
}

/** Synchronous fallback (static registry only) — prefer `resolveModelContextLimit`. */
export function getModelContextLimitSync(
  model: string,
  options?: { provider?: ModelContextProvider },
): number {
  const provider: ModelContextProvider =
    options?.provider ??
    (model.includes('/') || model.startsWith('~') ? 'openrouter' : 'gemini');
  const map = buildStaticFallback(provider);
  return (
    lookupContextLimit(map, model) ??
    (provider === 'openrouter' ? DEFAULT_OPENROUTER_CONTEXT_LIMIT : DEFAULT_GEMINI_CONTEXT_LIMIT)
  );
}



/** Test helper — reset in-memory caches. */
export function resetModelCatalogCachesForTests(): void {
  geminiCache = null;
  openRouterCache = null;
  geminiInflight = null;
  openRouterInflight = null;
}
